import { format } from '@lib/format';

export const NO_DATA = '—';

export const PAGE_SIZE = 50;

// Период списка и шапки адреса. suffix — окончание полей итогов шапки (count_7d, volume_usdt_all, …)
export const PERIOD_OPTIONS = [
  { value: 'D7', label: '7 дней', suffix: '7d' },
  { value: 'D30', label: '30 дней', suffix: '30d' },
  { value: 'D90', label: '90 дней', suffix: '90d' },
  { value: 'ALL', label: 'Всё время', suffix: 'all' },
];

export const DIRECTION_OPTIONS = [
  { value: 'ANY', label: 'Все направления' },
  { value: 'IN', label: 'Входящие' },
  { value: 'OUT', label: 'Исходящие' },
];

export const KIND_OPTIONS = [
  { value: 'ANY', label: 'Все виды' },
  { value: 'ADDRESS', label: 'Адреса' },
  { value: 'UID', label: 'UID Binance' },
];

// '' — «Все»: во вкладке «Транзакции» параметр не передаётся,
// во вкладке «Пользователи» уходит ALL (там по умолчанию SUCCESS).
// TIME_OUT не берём — это статус фиатных провайдеров, у крипто-операций его нет.
export const STATUS_OPTIONS = [
  { value: '', label: 'Все статусы' },
  { value: 'SUCCESS', label: 'SUCCESS' },
  { value: 'PENDING', label: 'PENDING' },
  { value: 'FAILED', label: 'FAILED' },
  { value: 'CANCELED', label: 'CANCELED' },
  { value: 'REJECTED', label: 'REJECTED' },
];

export const TX_TYPE_LABELS = {
  DEPOSIT: 'Депозит',
  WITHDRAW: 'Вывод',
};

export const DIRECTION_LABELS = {
  ANY: 'Всего',
  IN: 'Входящие',
  OUT: 'Исходящие',
};

export const KIND_LABELS = {
  ADDRESS: 'Адрес',
  UID: 'UID Binance',
};

// Ключ контрагента — сеть + адрес. В адресе TON бывают / и +, поэтому кодируем.
export const getAddressPath = (network, address) =>
  `/crypto-addresses/${network}/${encodeURIComponent(address)}`;

export const getUserPath = (userId) => `/user/view/${userId}`;

export const getTransactionPath = (pk, sk) =>
  `/transactions/transaction/details?pk=${encodeURIComponent(pk)}&sk=${encodeURIComponent(sk)}`;

// Даты в ответах — Unix-время в секундах
export const formatUnixTime = (seconds) =>
  seconds ? new Date(seconds * 1000).toLocaleString('ru-RU') : NO_DATA;

// '2026-10-21' -> '21.10.2026'
export const formatIsoDate = (value) => {
  if (!value) {
    return NO_DATA;
  }
  const [year, month, day] = String(value).split('-');
  return day && month && year ? `${day}.${month}.${year}` : value;
};

// Даты в запросах — dd/mm/yyyy
export const formatQueryDate = (date) =>
  date
    ? `${format.addZeroBefore(date.getDate())}/${format.addZeroBefore(date.getMonth() + 1)}/${date.getFullYear()}`
    : undefined;

const hasValue = (value) => value !== null && value !== undefined && value !== '';

export const formatCount = (value) => (hasValue(value) ? format.asInteger(value) : NO_DATA);

export const formatUsdt = (value) => (hasValue(value) ? format.asInteger(Number(value), 2) : NO_DATA);

// Сумма в активе приходит Decimal-строкой с хвостом нулей ("100.00000000") — срезаем хвост
export const formatAmount = (value) =>
  hasValue(value)
    ? format.asDecimal(String(value).replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, ''))
    : NO_DATA;

export const getUserName = (user) => {
  if (!user) {
    return NO_DATA;
  }
  const name = [user.first_name, user.last_name].filter(Boolean).join(' ');
  return name || user.phone || user.email || user.user_id || NO_DATA;
};

export const getErrorMessage = (error, fallback = 'Не удалось загрузить данные') => {
  if (error?.status === 418) {
    return 'Нет доступа к разделу. Права выдаёт администратор на странице ролей.';
  }
  const detail = error?.data?.detail;
  // Валидация тела FastAPI отдаёт detail массивом — показываем общий текст
  if (Array.isArray(detail)) {
    return 'Неверные данные запроса';
  }
  return typeof detail === 'string' && detail ? detail : fallback;
};

// KYT. Скор и доли приходят Decimal-строками ("93.00") — показываем без хвоста нулей.
// Пороги «опасно / безопасно» не решены: число не красим и не оцениваем.
export const formatRisk = formatAmount;

export const KYT_TAB = 'kyt';

export const getAddressKytPath = (network, address) => `${getAddressPath(network, address)}?tab=${KYT_TAB}`;

// Статусы KYT у транзакции (kyt_state)
export const KYT_STATE_LABELS = {
  CHECKED: 'Проверено',
  ERROR: 'Ошибка проверки',
  QUEUED: 'В очереди',
  AFTER: 'Проверено после операции',
  NOT_CHECKED: 'Не проверялось',
  NOT_APPLICABLE: 'Не применимо — внутренний перевод Binance',
};

export const KYT_CHECK_STATUS_LABELS = {
  checked: 'Проверено',
  error: 'Ошибка',
};

// Категории провайдера: русское название, если есть
export const getKytCategoryName = (item) => item?.name_ru || item?.name || NO_DATA;

// Блэклист адресов вывода. REVIEW ставит только ночной крон, сотрудник — BLOCKED или ALLOWED
export const BLACKLIST_TAB = 'blacklist';

export const BLACKLIST_STATUS_LABELS = {
  BLOCKED: 'В блоке',
  REVIEW: 'На рассмотрении',
  ALLOWED: 'Разрешён',
};

export const BLACKLIST_SOURCE_LABELS = {
  MANUAL: 'Вручную',
  AUTO: 'Автоматически (5+ клиентов)',
};

export const BLACKLIST_COMMENT_MAX_LENGTH = 1000;

export const getBlacklistStatusLabel = (status) => (status ? BLACKLIST_STATUS_LABELS[status] || status : NO_DATA);

// Первое событие адреса — from_status: null
export const formatBlacklistTransition = (from, to) =>
  `${getBlacklistStatusLabel(from)} → ${getBlacklistStatusLabel(to)}`;

// SYSTEM — ночной крон. Профиль сотрудника может не найтись — тогда показываем его id
export const getBlacklistAuthor = (by, byName) => {
  if (by === 'SYSTEM') {
    return 'Система';
  }
  return byName || by || NO_DATA;
};

// 'TRX#TGhm…' -> { network: 'TRX', address: 'TGhm…' }. Делим по первому #: в адресе с тегом # тоже бывает
export const splitCounterparty = (counterparty) => {
  const index = String(counterparty).indexOf('#');
  return { network: counterparty.slice(0, index), address: counterparty.slice(index + 1) };
};

const BISHKEK_TIME_ZONE = 'Asia/Bishkek';

export const formatBishkekTime = (seconds) =>
  seconds ? new Date(seconds * 1000).toLocaleString('ru-RU', { timeZone: BISHKEK_TIME_ZONE }) : NO_DATA;

// Сегодняшняя дата по Бишкеку как локальный Date — для выбора дат и formatQueryDate
export const getBishkekToday = () => {
  const [year, month, day] = new Intl.DateTimeFormat('en-CA', { timeZone: BISHKEK_TIME_ZONE })
    .format(new Date())
    .split('-')
    .map(Number);
  return new Date(year, month - 1, day);
};
